import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowRight, Battery, ChevronRight, Zap, Shield, Sparkles, Leaf, Phone,
  ClipboardList, CheckCircle, Wrench, Layers, FileText, Home, Gauge, Cpu,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';
import {
  getDeyePackage, DEYE_PACKAGES, availableInverters, startingPrice, fmtAUD,
  DEYE_PDU, DEYE_SMART_METER,
} from '../data/deyePackages';

const FAQS = [
  { q: 'What is included in a Deye battery package?', a: 'Each standard package includes one Deye hybrid inverter, one PDU base unit, Deye AI-W5.1-B battery modules (5.12 kWh each), and a smart meter — fully supplied and installed.' },
  { q: 'How many batteries can be included in one Deye stack?', a: 'One stack can include up to 6 Deye AI-W5.1-B battery modules, for a maximum of 30.72 kWh.' },
  { q: 'What battery sizes are available?', a: 'Package sizes range from 5.12 kWh (1 module) up to 30.72 kWh (6 modules).' },
  { q: 'Do I need a single-phase or three-phase inverter?', a: "This depends on your property's electrical supply and overall system requirements. Our team will confirm the right option for your home." },
  { q: 'Is the price final?', a: 'The advertised price is based on standard installation conditions. Additional costs may apply depending on your site requirements, which are confirmed after the site inspection.' },
  { q: 'What does the site inspection check?', a: 'The switchboard, battery location, cable run, wall surface, base/slab suitability, and any additional installation requirements.' },
  { q: 'What happens if additional costs are required?', a: 'They will be confirmed after the site inspection and before installation is organised — so you always know your final price before we proceed.' },
  { q: 'Is the BSTC rebate included in the price?', a: 'The shown out-of-pocket price assumes the customer assigns the applicable BSTC (Battery STC) rebate to Supa Energy Hub.' },
];

const whyChoose = (pkg) => {
  const k = pkg.kwh;
  if (k <= 5.12) return { tier: 'Entry-level storage for smaller homes', copy: 'A great starting point for smaller homes and households wanting to shift everyday usage into the evening and cut grid reliance. Easily expandable later by adding more AI-W5.1-B modules.' };
  if (k <= 10.24) return { tier: 'Popular entry upgrade', copy: 'Our most popular starting size — store enough daytime solar to power the evening peak and meaningfully reduce your power bills, with room to grow.' };
  if (k <= 15.36) return { tier: 'Family home', copy: 'Sized for a typical family home with solar — store more of your daytime solar to run the evening and overnight, with strong everyday coverage.' };
  if (k <= 20.48) return { tier: 'High-usage solar home', copy: 'Ideal for higher-consumption homes with larger solar, pools or EV charging that want strong overnight coverage and resilience.' };
  if (k <= 25.60) return { tier: 'Larger home', copy: 'Built for larger homes and all-electric households with high daily usage that want serious storage capacity and extended backup.' };
  return { tier: 'Large battery storage', copy: 'The maximum standard Deye AI-W5.1 stack — for large homes and high-consumption properties needing the most storage capacity and whole-home energy independence.' };
};

const INCLUSIONS = [
  'Deye AI-W5.1-B battery modules (per configuration)',
  'Deye hybrid inverter (chosen option)',
  'PDU base unit (matched to your system phase)',
  'Deye smart meter supply and installation',
  'Standard delivery',
  'Standard installation',
  'Easy access to battery installation location',
  'Easy access to switchboard',
  'Battery installed within 5 m of the switchboard',
  'Clear and unobstructed cabling pathway',
  'Switchboard has enough space for smart meter and required switches',
  'Existing flat, level, sturdy concrete base / slab available',
  'Suitable fire-resistant wall behind battery location',
];

const EXTRAS = [
  ['Bollards, if required', '$200 per site'],
  ['Awning, if required', '$200 per site'],
  ['Additional cabling beyond 5 m', '$25 per metre after the first 5 m'],
  ['Concrete slab / base, if required', '$150'],
  ['Fireproof backing / cement sheet, if required', '$150'],
  ['Additional switchboard works', 'To be estimated before installation'],
  ['Obstructed cabling pathway', 'Based on additional labour and materials'],
  ['Other site-specific works', 'Based on additional labour and materials'],
];

const DeyePackagePage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.deyeSlug;
  const pkg = getDeyePackage(slug);
  if (!pkg) {
    // Own the deye- namespace: invalid deye slugs go back to the overview.
    // Anything else renders nothing (preserves the app's existing 404 behaviour).
    return slug && slug.startsWith('deye-')
      ? <Navigate to="/deye-battery-packages" replace />
      : null;
  }

  const minPrice = startingPrice(pkg);
  const invs = availableInverters();
  const cheapest = invs.find((i) => pkg.prices[i.id] === minPrice);
  const others = DEYE_PACKAGES.filter((p) => p.slug !== pkg.slug);
  const why = whyChoose(pkg);

  return (
    <div className="min-h-screen">
      <SEO
        title={`Deye ${pkg.kwh.toFixed(2)} kWh Battery Package — From ${fmtAUD(minPrice)} Installed*`}
        description={`Deye AI-W5.1 ${pkg.kwh.toFixed(2)} kWh battery package (${pkg.modules} × AI-W5.1-B modules) fully supplied and installed by Supa Energy Hub. From ${fmtAUD(minPrice)} out-of-pocket after estimated BSTC discount. Single & three-phase Deye hybrid inverter options.`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
          },
        ]}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
          <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
          <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-5" data-testid="deye-breadcrumb">
              <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/deye-battery-packages" className="hover:text-[#3D5A47]">Deye Battery Packages</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#3D5A47] font-medium">{pkg.kwh.toFixed(2)} kWh</span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-8 items-stretch">
              <div className="lg:col-span-7 flex flex-col">
                <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-4 w-fit">
                  <Sparkles className="w-3.5 h-3.5 text-[#3D5A47]" />
                  <span className="text-[11px] font-bold tracking-wider uppercase text-[#3D5A47]">Deye AI-W5.1 Package · Fully Installed</span>
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight">
                  Deye <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">{pkg.kwh.toFixed(2)} kWh</span> Battery Package
                </h1>
                <p className="text-sm text-gray-500 mt-2 font-medium uppercase tracking-wide">
                  {pkg.modules} × AI-W5.1-B module{pkg.modules > 1 ? 's' : ''} · {pkg.position}
                </p>
                <p className="text-base sm:text-lg text-gray-700 mt-4 max-w-2xl leading-relaxed">
                  A fully supplied and installed Deye AI-W5.1 all-in-one battery system using {pkg.modules} × AI-W5.1-B LFP
                  module{pkg.modules > 1 ? 's' : ''}, a PDU base and a smart meter — paired with your choice of Deye hybrid
                  inverter and sized to your home and phase configuration.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link to={`/get-estimate?source=${pkg.slug}`}>
                    <Button className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-sm sm:text-base font-semibold px-5 py-5 rounded-xl shadow-lg shadow-[#3D5A47]/20" data-testid="deye-hero-cta">
                      Get My Deye Battery Quote <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0478453548">
                    <Button variant="outline" className="bg-white border-[#3D5A47]/20 text-[#3D5A47] hover:bg-[#f8faf5] gap-2 text-sm sm:text-base px-5 py-5 rounded-xl">
                      <Phone className="w-4 h-4" /> Call 0478 453 548
                    </Button>
                  </a>
                </div>
                <p className="text-[11px] text-gray-500 mt-4">
                  *Out-of-pocket price after estimated BSTC discount is assigned to Supa Energy Hub. Standard install conditions apply.
                </p>
              </div>

              {/* Product + Starting From card */}
              <div className="lg:col-span-5">
                <div className="bg-gradient-to-br from-[#1a2820] to-[#3D5A47] rounded-2xl shadow-xl overflow-hidden relative">
                  <div className="relative aspect-[16/12] bg-white flex items-center justify-center">
                    <img src={pkg.img} alt={`Deye AI-W5.1 ${pkg.kwh.toFixed(2)} kWh battery system`} className="w-full h-full object-contain p-4" />
                    <span className="absolute top-3 right-3 bg-[#B5D43B] text-[#1a2820] text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-md">
                      {pkg.kwh.toFixed(2)} kWh
                    </span>
                  </div>
                  <div className="p-6 text-white">
                    <p className="text-[11px] uppercase tracking-widest text-[#B5D43B] font-bold mb-1">Starting from</p>
                    <p className="text-5xl font-black leading-none" data-testid="deye-starting-price">{fmtAUD(minPrice)}</p>
                    <p className="text-xs text-white/70 mt-2">
                      Installed, out-of-pocket after estimated BSTC discount — with the <strong className="text-white">{cheapest?.short}</strong> inverter.
                    </p>
                    <div className="mt-5 pt-5 border-t border-white/10 grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="uppercase tracking-widest text-white/50 font-semibold mb-1">Battery size</p>
                        <p className="font-bold">{pkg.kwh.toFixed(2)} kWh</p>
                      </div>
                      <div>
                        <p className="uppercase tracking-widest text-white/50 font-semibold mb-1">Est. BSTC discount</p>
                        <p className="font-bold">{fmtAUD(pkg.bstcDiscount)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PACKAGE OVERVIEW */}
        <section className="py-14 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="deye-overview">
              {[
                { icon: Battery, t: `${pkg.modules} × AI-W5.1-B module${pkg.modules > 1 ? 's' : ''}`, d: `5.12 kWh each · ${pkg.kwh.toFixed(2)} kWh total · LFP` },
                { icon: Cpu, t: '1 × Deye hybrid inverter', d: 'Your chosen single- or three-phase inverter.' },
                { icon: Layers, t: '1 × PDU base unit', d: 'Connects and manages the stack with the inverter.' },
                { icon: Gauge, t: '1 × smart meter', d: 'Matched to your single- or three-phase system.' },
              ].map((f, i) => (
                <div key={i} className="bg-[#f8faf5] rounded-xl p-5 border border-gray-100">
                  <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-3">
                    <f.icon className="w-5 h-5 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{f.t}</h3>
                  <p className="text-xs text-gray-600">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* INVERTER OPTIONS / PRICING TABLE */}
        <section className="py-14 bg-[#f8faf5] border-y border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Choose your inverter</span>
            <h2 className="heading-2 text-[#3D5A47] mb-2">Available Deye inverter options</h2>
            <p className="text-gray-600 text-sm mb-6 max-w-3xl">
              The {pkg.kwh.toFixed(2)} kWh Deye AI-W5.1 battery pairs with the Deye hybrid inverter options below.
              Each price is your installed, out-of-pocket cost after the estimated BSTC discount. Choose based on your phase
              configuration and solar size.
            </p>
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm" data-testid="deye-inverter-table">
              <table className="w-full text-sm">
                <thead className="bg-[#3D5A47] text-white text-left">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Inverter Option</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Phase</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold text-right">Out-of-Pocket Price (after rebate*)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invs.map((inv) => {
                    const isMin = pkg.prices[inv.id] === minPrice;
                    return (
                      <tr key={inv.id} className={isMin ? 'bg-[#B5D43B]/10' : ''} data-testid={`deye-row-${inv.id}`}>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-[#3D5A47]" />
                            <span className="font-semibold text-[#3D5A47]">{inv.short}</span>
                            {isMin && (
                              <span className="text-[10px] uppercase tracking-widest bg-[#B5D43B] text-[#1a2820] font-bold px-2 py-0.5 rounded-full">Best Value</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-gray-600">{inv.phase === 3 ? 'Three-Phase' : 'Single-Phase'}</td>
                        <td className="px-4 sm:px-6 py-4 text-right font-bold text-[#3D5A47]">{fmtAUD(pkg.prices[inv.id])}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              *Prices assume standard installation and customer assignment of the applicable BSTC rebate (estimated {fmtAUD(pkg.bstcDiscount)} discount) to Supa Energy Hub. Additional site-specific costs may apply.
            </p>
          </div>
        </section>

        {/* WHY CHOOSE THIS PACKAGE */}
        <section className="py-14 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Why choose this package</span>
            <h2 className="heading-2 text-[#3D5A47] mb-3">{why.tier}</h2>
            <p className="text-gray-700 text-sm max-w-3xl leading-relaxed">{why.copy}</p>
            <div className="mt-5 inline-flex items-start gap-3 bg-[#f0f7e6] border border-[#B5D43B]/40 rounded-xl p-4 max-w-2xl">
              <Home className="w-5 h-5 text-[#3D5A47] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                This {pkg.kwh.toFixed(2)} kWh package uses {pkg.modules} stackable AI-W5.1-B module{pkg.modules > 1 ? 's' : ''}. Need more or less capacity? You can step up or down in clean 5.12 kWh increments up to the 30.72 kWh maximum.
              </p>
            </div>
          </div>
        </section>

        {/* WHAT'S INCLUDED */}
        <section className="py-14 bg-[#f8faf5] border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-[#3D5A47] mb-2">What&rsquo;s Included in the Standard Installation Price?</h2>
            <p className="text-gray-600 mb-6 text-sm">
              The advertised package price is based on a <strong>standard installation</strong>. Here&rsquo;s everything that&rsquo;s covered:
            </p>
            <ul className="grid sm:grid-cols-2 gap-3" data-testid="deye-included-list">
              {INCLUSIONS.map((item) => (
                <li key={item} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
                  <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 mt-6 italic max-w-3xl">
              Prices are based on standard installation conditions and customer assignment of the applicable BSTC rebate
              to Supa Energy Hub. Final price may vary depending on site conditions, switchboard requirements, battery
              location, cable run, wall surface, base/slab condition, safety requirements, and other installation factors.
            </p>
          </div>
        </section>

        {/* ADDITIONAL COSTS */}
        <section className="py-14 bg-[#fdf4ec] border-y border-orange-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="heading-2 text-[#3D5A47] mb-2">Additional Costs That May Apply</h2>
                <p className="text-gray-700 text-sm max-w-3xl">
                  <strong>Important Note:</strong> Additional costs may apply if required, including bollards at $200 per site,
                  awning at $200 per site, additional cabling beyond 5 m at $25 per metre, concrete slab/base at $150,
                  fireproof backing/cement sheet at $150, additional switchboard works, obstructed cabling pathway, or other
                  site-specific labour and materials. These will be confirmed after the site inspection before installation is organised.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden border border-orange-100 shadow-sm" data-testid="deye-extras-table">
              <table className="w-full text-sm">
                <thead className="bg-orange-50 text-[#3D5A47] text-left">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Additional Item</th>
                    <th className="px-4 sm:px-6 py-3 font-semibold">Additional Cost / Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {EXTRAS.map(([k, v]) => (
                    <tr key={k}>
                      <td className="px-4 sm:px-6 py-3 text-gray-700">{k}</td>
                      <td className="px-4 sm:px-6 py-3 font-medium text-[#3D5A47]">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* SITE INSPECTION DEPOSIT */}
        <section className="py-14 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6 sm:p-8" data-testid="deye-site-inspection">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-[#3D5A47]" />
                </div>
                <h2 className="heading-2 text-[#3D5A47]">Site Inspection Deposit</h2>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed max-w-3xl">
                Non-refundable deposit for a technician site inspection to confirm installation suitability and identify any
                additional costs, if required. If you proceed, this amount is deducted from your final balance payable.
                If you cancel after the inspection or after receiving any additional cost estimate, this amount is retained
                as the site inspection / cancellation fee.
              </p>
            </div>
          </div>
        </section>

        {/* BSTC / REBATE NOTE */}
        <section className="py-12 bg-[#3D5A47] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-start gap-4">
            <Leaf className="w-7 h-7 text-[#B5D43B] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold mb-2">Rebate / BSTC discount note</h2>
              <p className="text-white/80 text-sm leading-relaxed">
                The out-of-pocket prices shown assume the customer assigns the applicable BSTC (Battery Small-scale Technology
                Certificate) rebate to Supa Energy Hub. The estimated discount for this {pkg.kwh.toFixed(2)} kWh package is
                approximately <strong className="text-white">{fmtAUD(pkg.bstcDiscount)}</strong> ({pkg.bstcQty} certificates).
                Final certificate value can vary with market conditions and eligibility, confirmed at the time of quoting.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">FAQ</span>
            <h2 className="heading-2 text-[#3D5A47] mb-6">Deye battery package questions</h2>
            <Accordion type="single" collapsible className="w-full" data-testid="deye-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-[#3D5A47] font-semibold text-sm hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* OTHER DEYE PACKAGES */}
        <section className="py-14 bg-[#f8faf5] border-t border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Need more (or less) capacity?</span>
                <h2 className="heading-2 text-[#3D5A47]">Other Deye AI-W5.1 packages</h2>
              </div>
              <Link to="/deye-battery-packages" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1">
                All Deye packages <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="deye-other-packages">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  className="group bg-white rounded-xl border border-gray-100 hover:border-[#B5D43B] hover:shadow-md transition-all p-4 text-center"
                  data-testid={`deye-other-${p.slug}`}
                >
                  <p className="text-2xl font-black text-[#3D5A47] group-hover:text-[#B5D43B] leading-none">{p.kwh.toFixed(2)}</p>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-1">kWh</p>
                  <p className="text-xs text-gray-600 mt-2">From {fmtAUD(startingPrice(p))}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-black mb-3">Claim your {pkg.kwh.toFixed(2)} kWh Deye package</h2>
            <p className="text-white/80 mb-6">Free quote. We handle the BSTC rebate paperwork and install everything end-to-end.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={`/get-estimate?source=${pkg.slug}`}>
                <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#1a2820] gap-2 font-bold px-6 py-5 rounded-xl" data-testid="deye-cta-quote">
                  <ClipboardList className="w-4 h-4" /> Check My Battery Price
                </Button>
              </Link>
              <Link to={`/get-estimate?source=${pkg.slug}-inspection`}>
                <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 px-6 py-5 rounded-xl" data-testid="deye-cta-inspection">
                  <FileText className="w-4 h-4" /> Book Site Inspection
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DeyePackagePage;
